import { useMemo, useState } from 'react';

import { productionApi } from 'api/production';
import ColorInput from 'components/storefront/StorefrontConfigEditor/components/ColorInput';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Combobox } from 'shadcn/components/ui/Combobox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

/**
 * Generic Product Spotlight form component.
 *
 * Renders the form fields for a product spotlight: image, eyebrow, name, description,
 * link, product picker, and background color. This component is context-agnostic
 * and can be used in both the StorefrontConfigEditor and the AisleContent editor.
 *
 * @param {Object} props
 * @param {Object} props.data - Product spotlight data object
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling data changes
 * @param {Array} props.sellableProducts - Array of sellable products for the product picker
 * @returns {JSX.Element} ProductSpotlightForm component
 */
export default function ProductSpotlightForm({ data, disabled, onChange, sellableProducts = [] }) {
    const [imageError, setImageError] = useState(null);

    const handleChange = (e) => {
        onChange({ ...data, [e.target.name]: e.target.value });
    };

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ ...data, imageUrl });
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleClearImage = () => {
        onChange({ ...data, imageUrl: '' });
    };

    const handleProductChange = (externalSku) => {
        const product = sellableProducts.find((p) => p.externalSku === externalSku);
        onChange({ ...data, items: [product] });
    };

    const options = useMemo(
        () =>
            sellableProducts.map((item) => ({
                value: item.externalSku,
                label: `${item.externalSku}: ${item.name}`,
            })),
        [sellableProducts],
    );

    return (
        <div className="flex flex-col gap-4">
            <ImageInput
                id={`image-${data.id}`}
                imageUrl={data.imageUrl}
                error={imageError}
                onClearImage={handleClearImage}
                onImageUpload={handleImageUpload}
                disabled={disabled}
            />

            <div className="flex flex-col gap-2">
                <Label htmlFor={`eyebrow-${data.id}`}>Eyebrow</Label>
                <Input
                    type="text"
                    id={`eyebrow-${data.id}`}
                    name="eyebrow"
                    value={data.eyebrow ?? ''}
                    placeholder="Enter eyebrow"
                    onChange={handleChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`name-${data.id}`}>Name</Label>
                <Input
                    type="text"
                    id={`name-${data.id}`}
                    name="name"
                    value={data.name ?? ''}
                    placeholder="Enter name"
                    onChange={handleChange}
                    required
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`description-${data.id}`}>Description</Label>
                <Textarea
                    id={`description-${data.id}`}
                    name="description"
                    value={data.description ?? ''}
                    placeholder="Enter description"
                    onChange={handleChange}
                    rows="3"
                    disabled={disabled}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkText-${data.id}`}>Link Text</Label>
                    <Input
                        type="text"
                        id={`linkText-${data.id}`}
                        name="linkText"
                        value={data.linkText ?? ''}
                        placeholder="Enter link text"
                        onChange={handleChange}
                        disabled={disabled}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkUrl-${data.id}`}>Link URL</Label>
                    <Input
                        type="text"
                        id={`linkUrl-${data.id}`}
                        name="linkUrl"
                        value={data.linkUrl ?? ''}
                        placeholder="Enter link URL"
                        onChange={handleChange}
                        disabled={disabled}
                        pattern={CONTENT_LINK_URL_PATTERN}
                        title={CONTENT_LINK_URL_TITLE}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`productID-${data.id}`}>Product</Label>
                <Combobox
                    handleSelect={handleProductChange}
                    hideCheckmark
                    id={`productID-${data.id}`}
                    options={options}
                    value={data.items[0]?.external_sku || data.items[0]?.externalSku}
                    disabled={disabled}
                    commandProps={{
                        filter: (value, search) => value.toLowerCase().includes(search.toLowerCase()),
                    }}
                />
            </div>

            <ColorInput
                id={`bgColor-${data.id}`}
                label="Background Color"
                name="bgColor"
                value={data.bgColor}
                onChange={handleChange}
                disabled={disabled}
            />
        </div>
    );
}
